/**
 * @module useQuestions
 * @description Custom hook for managing questions and categories in the mental health portal.
 * Provides functionality for CRUD operations on questions, form handling,
 * and UI state management for both daily and weekly questions.
 */

/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from 'react';
import { useState } from 'react';
import api from '../../services/api';
import { useAuth } from 'react-oidc-context';
import { useTranslation } from 'react-i18next';

/**
 * @function useQuestions
 * @description Custom hook for managing questions and categories in the mental health portal
 * @returns {Object} Object containing state variables and methods for question and category management:
 * @returns {boolean} isLoading - Indicates if data is being loaded
 * @returns {Array} questions - List of all questions across all categories
 * @returns {Function} selectQuestion - Function to select a question by ID
 * @returns {Function} setSelectedQuestion - Function to directly set the selected question
 * @returns {Object|null} selectedQuestion - Currently selected question
 * @returns {Function} handleNewQuestionForm - Opens form for creating a new question
 * @returns {Function} setFilteredQuestions - Function to directly set filtered questions
 * @returns {Array} filteredQuestions - Questions filtered by the selected category/subcategory
 * @returns {boolean} isOpenQuestionForm - Indicates if question form is open
 * @returns {Function} handleOpenQuestionForm - Function to open the question form
 * @returns {Function} handleEditQuestionForm - Opens form for editing a question
 * @returns {Object} formQuestionData - Current data in the question form
 * @returns {Function} updateFormQuestionField - Updates a field in the question form
 * @returns {Function} handleSaveQuestionForm - Saves a new question
 * @returns {Function} handleCloseQuestionForm - Closes the question form
 * @returns {boolean} isOpenDeleteQuestionModal - Indicates if delete question modal is open
 * @returns {Function} handleOpenDeleteQuestionModal - Opens the delete question modal
 * @returns {Function} handleCloseDeleteQuestionModal - Closes the delete question modal
 * @returns {Function} handlerDeleteQuestionModal - Handles question deletion
 * @returns {Array} categories - List of all question categories
 * @returns {Object|null} selectedCategory - Currently selected category
 * @returns {Function} selectCategory - Function to select a category by ID
 * @returns {Object|null} selectedSubcategory - Currently selected subcategory
 * @returns {Function} selectSubcategory - Function to select a subcategory by ID
 * @returns {Function} handleUpdateQuestionForm - Updates an existing question
 * @returns {Object} formQuestionDataOriginal - Original data of the question being edited
 * @returns {boolean} showAlert - Indicates if alert is visible
 * @returns {Function} handleCloseAlert - Closes the alert
 * @returns {string} alertMessage - Message to display in the alert
 * @returns {string} typeAlert - Type of alert (success, error, etc.)
 * @returns {boolean} isValidFormQuestion - Indicates if question form is valid
 * @returns {Object} errorsFormQuestion - Contains validation errors for question form
 */
export const useQuestions = () => {
  const { t } = useTranslation();

  const auth = useAuth();
  const authId = auth.user?.profile.sub;

  /**
   * @constant {Object} initialState
   * @description Initial state for the hook
   */
  const initialState = {
    isLoading: true,
    questions: [],
    selectedQuestion: null,
    filteredQuestions: [],
    isOpenQuestionForm: false,
    isOpenDeleteQuestionModal: false,
    formQuestionData: {
      id: null,
      category_id: null,
      subcategory_id: null,
      title: null,
      question: null,
      created_by: null,
      updated_by: null,
    },
    isValidFormQuestion: true,
    errorsFormQuestion: {
      title: false,
      category_id: false,
      subcategory_id: false,
      question: false,
    },
    categories: [],
    isGetCategories: false,
    selectedCategory: null,
    isOpenCategoryForm: false,
    isOpenDeleteCategoryForm: false,
    showAlert: false,
    typeAlert: 'success',
    alertMessage: '',
  };

  // General states
  const [isLoading, setIsLoading] = useState(initialState.isLoading);
  const [showAlert, setShowAlert] = useState(initialState.showAlert);
  const [alertMessage, setAlertMessage] = useState(initialState.alertMessage);
  const [typeAlert, setTypeAlert] = useState(initialState.typeAlert);

  // Question states
  const [questions, setQuestions] = useState(initialState.questions);
  const [selectedQuestion, setSelectedQuestion] = useState(
    initialState.selectedQuestion
  );
  const [filteredQuestions, setFilteredQuestions] = useState(
    initialState.filteredQuestions
  );
  const [isOpenQuestionForm, setIsOpenQuestionForm] = useState(
    initialState.isOpenQuestionForm
  );
  const [isOpenDeleteQuestionModal, setIsOpenDeleteQuestionModal] = useState(
    initialState.isOpenDeleteQuestionModal
  );
  const [formQuestionData, setFormQuestionData] = useState(
    initialState.formQuestionData
  );
  const [isValidFormQuestion, setIsValidFormQuestion] = useState(
    initialState.isValidFormQuestion
  );
  const [errorsFormQuestion, setErrorsFormQuestion] = useState(
    initialState.errorsFormQuestion
  );
  const [formQuestionDataOriginal, setFormQuestionDataOriginal] = useState(
    initialState.formQuestionData
  );

  // Category states
  const [categories, setCategories] = useState(initialState.categories);
  const [isGetCategories, setIsGetCategories] = useState(
    initialState.isGetCategories
  );
  const [selectedCategory, setSelectedCategory] = useState(
    initialState.selectedCategory
  );
  const [selectedSubcategory, setSelectedSubcategory] = useState(
    initialState.selectedCategory
  );

  /**
   * @effect
   * @description Fetch categories on component mount
   */
  useEffect(() => {
    const fetchData = async () => {
      await readCategories();
    };

    fetchData();
  }, []);

  /**
   * @effect
   * @description Fetch questions when categories are loaded
   */
  useEffect(() => {
    const fetchData = async () => {
      await readQuestions();
    };

    if (isGetCategories) {
      fetchData();
    }
  }, [categories]);

  /**
   * @effect
   * @description Select initial category and subcategory when questions are loaded
   */
  useEffect(() => {
    if (categories.length > 0) {
      if (categories[0].subcategories?.length > 0) {
        selectSubcategory(categories[0].subcategories[0].id);
      } else {
        selectCategory(categories[0].id);
      }
    }
  }, [questions]);

  /**
   * @effect
   * @description Update loading state when filtered questions change
   */
  useEffect(() => {
    if (isGetCategories) {
      setIsLoading(false);
      setIsGetCategories(false);
    }
  }, [filteredQuestions]);

  /**
   * @function handleCloseAlert
   * @description Close the alert and reset alert message
   */
  const handleCloseAlert = () => {
    setShowAlert(false);
    setAlertMessage(initialState.alertMessage);
  };

  // QUESTION METHODS
  /**
   * @function selectQuestion
   * @description Select a question by ID and update the selected question state
   * @param {string|number} questionId - ID of the question to select
   * @returns {Object|null} The selected question object or null if not found
   */
  const selectQuestion = (questionId) => {
    const question = filteredQuestions.find(
      (question) => question.id === questionId
    );

    setSelectedQuestion(question);
    return question;
  };

  /**
   * @function resetFormQuestionData
   * @description Reset the question form data to initial state
   */
  const resetFormQuestionData = () => {
    setFormQuestionData(initialState.formQuestionData);
  };

  /**
   * @function handleOpenQuestionForm
   * @description Open the question form
   */
  const handleOpenQuestionForm = () => {
    setIsOpenQuestionForm(true);
  };

  /**
   * @function handleNewQuestionForm
   * @description Reset form data and open the question form for a new question
   */
  const handleNewQuestionForm = () => {
    resetFormQuestionData();
    setIsOpenQuestionForm(true);
  };

  /**
   * @function handleEditQuestionForm
   * @description Select a question and open the form for editing
   * @param {string|number} questionId - ID of the question to edit
   */
  const handleEditQuestionForm = (questionId) => {
    const question = selectQuestion(questionId);
    setFormQuestionDataOriginal({ ...question });
    setFormQuestionData({ ...question });
    setIsOpenQuestionForm(true);
  };

  /**
   * @function updateFormQuestionField
   * @description Update a specific field in the question form
   * @param {string} field - Field name to update
   * @param {*} value - New value for the field
   */
  const updateFormQuestionField = (field, value) => {
    setFormQuestionData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /**
   * @function handleCloseQuestionForm
   * @description Close the question form and reset form state
   */
  const handleCloseQuestionForm = () => {
    resetFormQuestionData();
    setIsValidFormQuestion(initialState.isValidFormQuestion);
    setErrorsFormQuestion(initialState.errorsFormQuestion);
    setIsOpenQuestionForm(false);
  };

  /**
   * @function handleSaveQuestionForm
   * @description Validate and save a new question
   * @param {Object} questionData - Question data to save
   */
  const handleSaveQuestionForm = async (questionData) => {
    try {
      const isValid = validFormQuestion();

      if (isValid) {
        setIsLoading(true);
        await saveQuestion(questionData);
        await readCategories();
        handleCloseQuestionForm();
        setTypeAlert('success');
        setAlertMessage(t('question created successfully'));
        setShowAlert(true);
      }
    } catch (error) {
      console.error(`handleSaveQuestionForm: ${error.message}`);
      handleCloseQuestionForm();
      setTypeAlert('error');
      setAlertMessage(t('error creating question'));
      setShowAlert(true);
      setIsLoading(false);
    }
  };

  /**
   * @function handleUpdateQuestionForm
   * @description Validate and update an existing question
   * @param {Object} questionData - Question data to update
   * @param {Object} formQuestionDataOriginal - Original question data before editing
   */
  const handleUpdateQuestionForm = async (
    questionData,
    formQuestionDataOriginal
  ) => {
    try {
      const isValid = validFormQuestion();

      if (isValid) {
        setIsLoading(true);
        await updateQuestion(questionData, formQuestionDataOriginal);
        await readCategories();
        handleCloseQuestionForm();
        setTypeAlert('success');
        setAlertMessage(t('question updated successfully'));
        setShowAlert(true);
      }
    } catch (error) {
      console.error(`handleUpdateQuestionForm: ${error.message}`);
      handleCloseQuestionForm();
      setTypeAlert('error');
      setAlertMessage(t('error updating question'));
      setShowAlert(true);
      setIsLoading(false);
    }
  };

  /**
   * @function handleOpenDeleteQuestionModal
   * @description Open the delete question confirmation modal
   */
  const handleOpenDeleteQuestionModal = () => {
    setIsOpenDeleteQuestionModal(true);
  };

  /**
   * @function handleCloseDeleteQuestionModal
   * @description Close the delete question confirmation modal
   */
  const handleCloseDeleteQuestionModal = () => {
    setIsOpenDeleteQuestionModal(false);
  };

  /**
   * @function handlerDeleteQuestionModal
   * @description Delete a question and handle the UI updates
   * @param {Object} question - Question object to delete
   */
  const handlerDeleteQuestionModal = async (question) => {
    try {
      setIsLoading(true);
      await deleteQuestion(question);
      await readCategories();
      setTypeAlert('success');
      setAlertMessage(t('question deletion successful'));
      setShowAlert(true);
      handleCloseDeleteQuestionModal();
    } catch (error) {
      handleCloseDeleteQuestionModal();
      setTypeAlert('error');
      setAlertMessage(t('error deleting question'));
      setShowAlert(true);
      setIsLoading(false);
    }
  };

  /**
   * @function readQuestions
   * @description Fetch all questions from the API for all categories
   */
  const readQuestions = async () => {
    const allQuestions = [];

    const dailyCategory = categories.find((cat) => cat.name === 'Daily');
    if (!dailyCategory || !dailyCategory.subcategories) {
      setFilteredQuestions(initialState.filteredQuestions);
      return;
    }

    for await (const subcategory of dailyCategory.subcategories) {
      const dailyQuestions = [];
      let hasNextPage = true;
      let currentOffset = 0;
      const limit = 10;

      while (hasNextPage) {
        const { data } = await api.get(
          `questions/daily?subcategoryId=${subcategory.id}&limit=${limit}&offset=${currentOffset}`
        );

        const { questions, pagination } = data;

        dailyQuestions.push(...questions);

        hasNextPage = pagination.hasNextPage;
        currentOffset += limit;
      }

      const dailyQuestionWithSubcategory = dailyQuestions.map(
        (dailyQuestion) => ({
          ...dailyQuestion,
          category_id: dailyCategory.id,
          subcategory_id: dailyQuestion.category_id,
        })
      );

      allQuestions.push(...dailyQuestionWithSubcategory);
    }

    const weeklyCategory = categories.find((cat) => cat.name === 'Weekly');
    if (!weeklyCategory) return;

    const weeklyQuestions = [];
    let hasNextPage = true;
    let currentOffset = 0;
    const limit = 10;

    while (hasNextPage) {
      const { data } = await api.get(
        `questions/weekly?userId=${authId}&limit=${limit}&offset=${currentOffset}`
      );

      const { questions, pagination } = data;

      weeklyQuestions.push(...questions);

      hasNextPage = pagination.hasNextPage;
      currentOffset += limit;
    }

    const weeklyQuestionWithSubcategory = weeklyQuestions.map(
      (weeklyQuestion) => ({
        ...weeklyQuestion,
        category_id: weeklyCategory.id,
        subcategory_id: weeklyQuestion.category_id,
      })
    );

    allQuestions.push(...weeklyQuestionWithSubcategory);

    setQuestions(allQuestions);
  };

  /**
   * @function saveQuestion
   * @description Save a new question to the API
   * @param {Object} questionData - Question data to save
   */
  const saveQuestion = async (questionData) => {
    if (!!questionData && questionData.category_id === 'daily') {
      await api.post('questions/daily', {
        category_id: questionData.subcategory_id,
        title: questionData.title,
        question: questionData.question,
      });
      return;
    }

    if (!!questionData && questionData.category_id === 'weekly') {
      await api.post('questions/weekly', {
        title: questionData.title,
        question: questionData.question,
      });
      return;
    }
  };

  /**
   * @function updateQuestion
   * @description Update an existing question via the API
   * @param {Object} questionData - Question data to update
   * @param {Object} formQuestionDataOriginal - Original question data before editing
   */
  const updateQuestion = async (questionData, formQuestionDataOriginal) => {
    await api.put(`questions/${questionData.id}`, {
      category_id: questionData.subcategory_id
        ? questionData.subcategory_id
        : null,
      title: questionData.title,
      question: questionData.question,
      fromTable: formQuestionDataOriginal.category_id,
      toTable: questionData.category_id,
    });
  };

  /**
   * @function deleteQuestion
   * @description Delete a question via the API
   * @param {Object} question - Question object to delete
   */
  const deleteQuestion = async (question) => {
    if (!!question && question.category_id === 'daily') {
      await api.delete(`questions/daily/${question.id}`);
      return;
    }

    if (!!question && question.category_id === 'weekly') {
      await api.delete(`questions/weekly/${question.id}`);
      return;
    }
  };

  // CATEGORY METHODS
  /**
   * @function selectCategory
   * @description Select a category by ID and update the selected category state
   * @param {string|number} categoryId - ID of the category to select
   * @returns {Object|null} The selected category object or null if not found
   */
  const selectCategory = (categoryId) => {
    const category = categories.find((cat) => cat.id === categoryId);
    const hasSubcategories = category.subcategories.length > 0;

    if (!categoryId || !category) {
      setSelectedCategory(null);
      setSelectedQuestion(null);
      return null;
    }

    if (hasSubcategories) {
      setSelectedCategory(category);
      return category;
    }

    selectSubcategory(null);
    setSelectedCategory(category);
    const filteredQuestions = filterQuestionsByCategory(category.id);
    setSelectedQuestion(filteredQuestions[0] ? filteredQuestions[0] : null);

    return category;
  };

  /**
   * @function selectSubcategory
   * @description Select a subcategory by ID and update the selected subcategory state
   * @param {string|number} subcategoryId - ID of the subcategory to select
   * @returns {Object|null} The selected subcategory object or null if not found
   */
  const selectSubcategory = (subcategoryId) => {
    const { category, subcategory } = categories.reduce(
      (acc, category) => {
        if (acc.category) return acc;

        const sub = category.subcategories?.find(
          (sub) => sub.id === subcategoryId
        );
        if (sub) {
          return { category, subcategory: sub };
        }

        return acc;
      },
      { category: null, subcategory: null }
    );

    if (!category) {
      setSelectedCategory(null);
      setSelectedSubcategory(null);
      setSelectedQuestion(null);

      return null;
    }

    if (!subcategory) {
      selectCategory(category.id);
      setSelectedSubcategory(null);
      setSelectedQuestion(null);

      return null;
    }

    selectCategory(category.id);
    setSelectedSubcategory(subcategory);

    const filteredQuestions = filterQuestionsBySubcategory(subcategory.id);
    setSelectedQuestion(filteredQuestions[0] ? filteredQuestions[0] : null);

    return subcategory;
  };

  /**
   * @function filterQuestionsByCategory
   * @description Filter questions by category ID
   * @param {string|number} categoryId - ID of the category to filter by
   * @returns {Array|null} Filtered questions array or null if no category ID
   */
  const filterQuestionsByCategory = (categoryId) => {
    if (!categoryId) {
      setFilteredQuestions(null);

      return null;
    }

    const filteredQuestions = questions.filter(
      (q) => q.category_id === categoryId
    );

    filteredQuestions.sort(
      (a, b) => new Date(a.updated_at) - new Date(b.updated_at)
    );

    setFilteredQuestions(filteredQuestions);

    return filteredQuestions;
  };

  /**
   * @function filterQuestionsBySubcategory
   * @description Filter questions by subcategory ID
   * @param {string|number} subcategoryId - ID of the subcategory to filter by
   * @returns {Array|null} Filtered questions array or null if no subcategory ID
   */
  const filterQuestionsBySubcategory = (subcategoryId) => {
    if (!subcategoryId) {
      setFilteredQuestions(null);
      return null;
    }

    const filteredQuestions = questions.filter(
      (q) => q.subcategory_id === subcategoryId
    );

    filteredQuestions.sort(
      (a, b) => new Date(b.updated_at) - new Date(a.updated_at)
    );

    setFilteredQuestions(filteredQuestions);

    return filteredQuestions;
  };

  /**
   * @function readCategories
   * @description Fetch all categories from the API
   */
  const readCategories = async () => {
    try {
      const { data } = await api.get('questions/categories');

      const { categories } = data;

      categories.sort(
        (a, b) => new Date(a.updated_at) - new Date(b.updated_at)
      );

      setCategories(categories);
      setIsGetCategories(true);
    } catch (error) {
      console.error('readCategories:', error.message);
      setCategories([]);
      setIsGetCategories(true);
    }
  };

  /**
   * @function validFormQuestion
   * @description Validate the question form data
   * @returns {boolean} True if form is valid, false otherwise
   */
  const validFormQuestion = () => {
    const errors = initialState.errorsFormQuestion;

    const { category_id, subcategory_id, title, question } = formQuestionData;

    if (!title) errors.title = true;
    if (!question) errors.question = true;
    if (!category_id) errors.category_id = true;
    if (!subcategory_id && category_id === 'daily') {
      errors.subcategory_id = true;
    }

    if (
      !title ||
      !question ||
      !category_id ||
      (!subcategory_id && category_id === 'daily')
    ) {
      setErrorsFormQuestion(errors);
      setIsValidFormQuestion(false);
      setTypeAlert('error');
      setAlertMessage(t('Please fill in all required fields.'));
      setShowAlert(true);
      return false;
    }

    setIsValidFormQuestion(true);
    setErrorsFormQuestion(initialState.errorsFormQuestion);
    setTypeAlert(initialState.typeAlert);
    setShowAlert(initialState.showAlert);
    return true;
  };

  return {
    isLoading,
    questions,
    selectQuestion,
    setSelectedQuestion,
    selectedQuestion,
    handleNewQuestionForm,
    setFilteredQuestions,
    filteredQuestions,
    isOpenQuestionForm,
    handleOpenQuestionForm,
    handleEditQuestionForm,
    formQuestionData,
    updateFormQuestionField,
    handleSaveQuestionForm,
    handleCloseQuestionForm,
    isOpenDeleteQuestionModal,
    handleOpenDeleteQuestionModal,
    handleCloseDeleteQuestionModal,
    handlerDeleteQuestionModal,
    categories,
    selectedCategory,
    selectCategory,
    selectedSubcategory,
    selectSubcategory,
    handleUpdateQuestionForm,
    formQuestionDataOriginal,
    showAlert,
    handleCloseAlert,
    alertMessage,
    typeAlert,
    isValidFormQuestion,
    errorsFormQuestion,
  };
};

export default useQuestions;
